import { queryOptions } from '@tanstack/react-query';
import { type AccessUser, permissions, type ProfileUser, type UserRole } from '@utilities-one/api-sdk';

import { companyTypeNames } from '@/config/company/companyTypeNames';
import { userStatusNames } from '@/config/users';
import type { UserWithOwnCompany } from '@/entities/user/types';
import { hasPermission } from '@/entities/user/utils/access';
import { api, parseResponse } from '@/library/api/honoClient';
import type { GetAllFilter, GetAllQuery } from '@/library/db/types';

export const userQueryKeys = {
  all: ['users'] as const,
  grids: () => [...userQueryKeys.all, 'grid'] as const,
  grid: (query: GetAllQuery) => [...userQueryKeys.grids(), query] as const,
  gridTotals: () => [...userQueryKeys.all, 'total'] as const,
  gridTotal: (filter: GetAllFilter) => [...userQueryKeys.gridTotals(), filter] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...userQueryKeys.details(), id] as const,
  selectType: () => [...userQueryKeys.all, 'selectType'] as const,
  session: () => [...userQueryKeys.all, 'session'] as const,
  accessUser: (userId: string, sessionId: string) => [...userQueryKeys.all, 'accessUser', userId, sessionId] as const,

  // Simple (no params)
  allNames: () => [...userQueryKeys.all, 'allNames'] as const,
  accountants: () => [...userQueryKeys.all, 'accountants'] as const,
  cc: () => [...userQueryKeys.all, 'cc'] as const,
  coordinatorsByPermissions: () => [...userQueryKeys.all, 'coordinatorsByPermissions'] as const,
  foremanByPermission: () => [...userQueryKeys.all, 'foremanByPermission'] as const,
  allOpm: () => [...userQueryKeys.all, 'allOpm'] as const,
  productionManagers: () => [...userQueryKeys.all, 'productionManagers'] as const,
  productionManagersByPermissions: () => [...userQueryKeys.all, 'productionManagersByPermissions'] as const,
  technicianByPermission: () => [...userQueryKeys.all, 'technicianByPermission'] as const,
  internalUsers: () => [...userQueryKeys.all, 'internalUsers'] as const,
  vp: () => [...userQueryKeys.all, 'vp'] as const,
  dailyCheckInUsers: () => [...userQueryKeys.all, 'dailyCheckInUsers'] as const,
  technicians: (filter?: GetAllFilter) => [...userQueryKeys.all, 'technicians', filter ?? {}] as const,

  // Parameterized
  coordinators: (directorId?: string) => [...userQueryKeys.all, 'coordinators', directorId ?? 'none'] as const,
  directors: (marketId?: string) => [...userQueryKeys.all, 'directors', marketId] as const,
  directorByProjectAllNames: (projectId: string) =>
    [...userQueryKeys.all, 'directorByProjectAllNames', projectId] as const,
  opmsByProjectAllNames: (projectId: string) => [...userQueryKeys.all, 'opmsByProjectAllNames', projectId] as const,
  foremansByProjectId: (projectId: string) => [...userQueryKeys.all, 'foremansByProjectId', projectId] as const,
  foremans: (filter?: Record<string, unknown>) => [...userQueryKeys.all, 'foremans', filter] as const,
  opms: (directorId?: string) => [...userQueryKeys.all, 'opms', directorId] as const,
  byRoles: (roles: UserRole[]) => [...userQueryKeys.all, 'byRoles', roles] as const,
  byParentId: (parentId: string) => [...userQueryKeys.all, 'byParentId', parentId] as const,
  teammates: (userId: string) => [...userQueryKeys.all, 'teammates', userId] as const,
  allByCompanyId: (companyId: string) => [...userQueryKeys.all, 'allByCompanyId', companyId] as const,
  selectStatus: () => [...userQueryKeys.all, 'select', 'status'] as const,
  selectPermissionsType: () => [...userQueryKeys.all, 'select', 'permissionsType'] as const,
};

type CompanyJson = {
  earlyPaymentActiveFrom: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

const reviveCompanyDates = <T extends CompanyJson>(company: T) => ({
  ...company,
  earlyPaymentActiveFrom: company.earlyPaymentActiveFrom ? new Date(company.earlyPaymentActiveFrom) : null,
  createdAt: new Date(company.createdAt),
  updatedAt: new Date(company.updatedAt),
  deletedAt: company.deletedAt ? new Date(company.deletedAt) : null,
});

export const fetchUserById = (id: string): Promise<UserWithOwnCompany | null> =>
  parseResponse(api.users.getById.$post({ json: { id } })).then((user) =>
    user
      ? {
          ...user,
          createdAt: new Date(user.createdAt),
          updatedAt: new Date(user.updatedAt),
          deletedAt: user.deletedAt ? new Date(user.deletedAt) : null,
          role: user.role
            ? { ...user.role, createdAt: new Date(user.role.createdAt), updatedAt: new Date(user.role.updatedAt) }
            : null,
          parent: user.parent
            ? {
                ...user.parent,
                createdAt: new Date(user.parent.createdAt),
                updatedAt: new Date(user.parent.updatedAt),
                deletedAt: user.parent.deletedAt ? new Date(user.parent.deletedAt) : null,
              }
            : null,
          children: user.children.map((child) => ({
            ...child,
            createdAt: new Date(child.createdAt),
            updatedAt: new Date(child.updatedAt),
            deletedAt: child.deletedAt ? new Date(child.deletedAt) : null,
          })),
          company: {
            ...reviveCompanyDates(user.company),
            users: user.company.users.map((companyUser) => ({
              ...companyUser,
              createdAt: new Date(companyUser.createdAt),
              updatedAt: new Date(companyUser.updatedAt),
              deletedAt: companyUser.deletedAt ? new Date(companyUser.deletedAt) : null,
            })),
          },
          ownCompany: user.ownCompany ? reviveCompanyDates(user.ownCompany) : null,
        }
      : null
  ) as Promise<UserWithOwnCompany | null>;

export const fetchUsersByParentId = (parentId: string): Promise<ProfileUser[]> =>
  parseResponse(api.users.getByParentId.$post({ json: { parentId } })).then((items) =>
    items.map((item) => ({
      ...item,
      role: item.role
        ? { ...item.role, createdAt: new Date(item.role.createdAt), updatedAt: new Date(item.role.updatedAt) }
        : null,
    }))
  ) as Promise<ProfileUser[]>;

export const fetchTeammates = (params: {
  userId: string;
  parentId: string | null;
  companyId: string;
  roleId: string;
}): Promise<ProfileUser[]> =>
  parseResponse(api.users.getTeammates.$post({ json: params })).then((items) =>
    items.map((item) => ({
      ...item,
      role: item.role
        ? { ...item.role, createdAt: new Date(item.role.createdAt), updatedAt: new Date(item.role.updatedAt) }
        : null,
    }))
  ) as Promise<ProfileUser[]>;

export const fetchUsersByRoles = (roles: UserRole[]) => parseResponse(api.users.getByRoles.$post({ json: { roles } }));

export const fetchUserAllNames = () => parseResponse(api.users.getAllNames.$post());

/**
 * Options for the users grid Type filter: the employer company types plus the user-level
 * W-2 mark (UG-5002 — W-2 is a User flag from UG-4866, not a company type).
 */
export const userTypeSelectQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.selectType(),
    queryFn: () =>
      Promise.resolve([
        ...Object.entries(companyTypeNames).map(([id, name]) => ({ id, name })),
        { id: 'w2', name: 'W-2' },
      ]),
  });

export const userGridQueryOptions = (query: GetAllQuery) =>
  queryOptions({
    queryKey: userQueryKeys.grid(query),
    queryFn: () => parseResponse(api.users.getAll.$post({ json: query })),
  });

export const userGridTotalQueryOptions = (filter: GetAllFilter) =>
  queryOptions({
    queryKey: userQueryKeys.gridTotal(filter),
    queryFn: () => parseResponse(api.users.getTotal.$post({ json: filter })),
  });

// Grid via Hono API (AgGrid)
export const userAgGridQueryOptions = (query: GetAllQuery) =>
  queryOptions({
    queryKey: [...userQueryKeys.grid(query), 'api'] as const,
    queryFn: () => parseResponse(api.users.getAll.$post({ json: query })),
  });

export const userAgGridTotalQueryOptions = (filter: GetAllFilter) =>
  queryOptions({
    queryKey: [...userQueryKeys.gridTotal(filter), 'api'] as const,
    queryFn: () => parseResponse(api.users.getTotal.$post({ json: filter })),
  });

export const userAgGridUnifiedQueryOptions = (query: GetAllQuery) =>
  queryOptions({
    queryKey: [...userQueryKeys.grid(query), 'api', 'unified'] as const,
    queryFn: async () => {
      const [items, total] = await Promise.all([
        parseResponse(api.users.getAll.$post({ json: query })),
        parseResponse(api.users.getTotal.$post({ json: query.filter })),
      ]);
      return [items, total] as const;
    },
  });

// Detail
export const userDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userQueryKeys.detail(id),
    queryFn: () => fetchUserById(id),
    enabled: !!id,
  });

/** Current browser session identity (`getSessionUser`). Used by `AccessProvider`. */
export const userSessionUserQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.session(),
    queryFn: () => parseResponse(api.users.getSessionUser.$post()),
  });

/** Full `AccessUser` for the signed-in session (user + session flags). */
export const userAccessUserQueryOptions = (params: { userId: string; sessionId: string }) =>
  queryOptions({
    queryKey: userQueryKeys.accessUser(params.userId, params.sessionId),
    queryFn: async (): Promise<AccessUser | null> => {
      try {
        const sessionUser = await fetchUserById(params.userId);
        if (!sessionUser) return null;

        const sessionDetails = await parseResponse(api.sessions.getUserSession.$post({}));

        let accessUser: AccessUser = { ...sessionUser, sessionId: params.sessionId };

        if (hasPermission(accessUser, permissions.projects.view.asTechnician)) {
          try {
            const resolved = await parseResponse(api.users.resolveTechnicianPermissions.$post({}));

            accessUser = { ...accessUser, permissions: resolved.permissions as typeof accessUser.permissions };
          } catch (error) {
            console.error('Failed to resolve parent permissions:', error);
          }
        }

        accessUser.isImpersonating = sessionDetails?.impersonatedById != null;

        return accessUser;
      } catch (error) {
        console.error('Failed to sync session user:', error);
        return null;
      }
    },
  });

// Simple
export const userAllNamesQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.allNames(),
    queryFn: () => fetchUserAllNames(),
  });

export const userAccountantsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.accountants(),
    queryFn: () =>
      parseResponse(api.users.getAccountants.$post()).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
  });

export const userCCQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.cc(),
    queryFn: () => parseResponse(api.users.getCC.$post()),
  });

export const userCoordinatorsByPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.coordinatorsByPermissions(),
    queryFn: () =>
      parseResponse(api.users.getCoordinatorsByPermissions.$post()).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
  });

export const userForemanByPermissionQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.foremanByPermission(),
    queryFn: () =>
      parseResponse(api.users.getForemanByPermission.$post()).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
  });

export const userAllOpmQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.allOpm(),
    queryFn: () => parseResponse(api.users.getAllOpm.$post()),
  });

export const userProductionManagersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.productionManagers(),
    queryFn: () =>
      parseResponse(api.users.getProductionManagers.$post()).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
  });

export const userProductionManagersByPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.productionManagersByPermissions(),
    queryFn: () =>
      parseResponse(api.users.getProductionManagersByPermissions.$post()).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
  });

export const userTechnicianByPermissionQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.technicianByPermission(),
    queryFn: () => parseResponse(api.users.getTechnicianByPermission.$post()),
  });

export const userInternalUsersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.internalUsers(),
    queryFn: () => parseResponse(api.users.getInternalUsers.$post()),
  });

export const userVPQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.vp(),
    queryFn: () => parseResponse(api.users.getVP.$post()),
  });

export const userDailyCheckInUsersQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.dailyCheckInUsers(),
    queryFn: () => parseResponse(api.users.getDailyCheckInUsers.$post()),
  });

export const userTechniciansQueryOptions = (filter?: GetAllFilter) =>
  queryOptions({
    queryKey: userQueryKeys.technicians(filter),
    queryFn: () => parseResponse(api.users.getTechnicians.$post({ json: { filter } })),
  });

// Parameterized

export const userCoordinatorsQueryOptions = (directorId?: string) =>
  queryOptions({
    queryKey: userQueryKeys.coordinators(directorId),
    queryFn: () => parseResponse(api.users.getCoordinators.$post({ json: { directorId } })),
  });

export const userDirectorsQueryOptions = (marketId?: string, ensureItem?: { id: string; name: string }) =>
  queryOptions({
    queryKey: [...userQueryKeys.directors(marketId), 'ensure', ensureItem?.id ?? null] as const,
    queryFn: async () => {
      const list = await parseResponse(api.users.getDirectors.$post({ json: { marketId } }));
      if (!ensureItem || list.some((d) => d.id === ensureItem.id)) return list;
      return [...list, ensureItem].sort((a, b) => a.name.localeCompare(b.name));
    },
  });

export const userDirectorByProjectAllNamesQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: userQueryKeys.directorByProjectAllNames(projectId),
    queryFn: () => parseResponse(api.users.getDirectorByProjectAllNames.$post({ json: { projectId } })),
    enabled: !!projectId,
  });

/** The OPMs assigned on the project's definition (UG-5237). */
export const userOpmsByProjectAllNamesQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: userQueryKeys.opmsByProjectAllNames(projectId),
    queryFn: () => parseResponse(api.users.getOpmsByProjectAllNames.$post({ json: { projectId } })),
    enabled: !!projectId,
  });

export const userForemansByProjectIdQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: userQueryKeys.foremansByProjectId(projectId),
    queryFn: () =>
      parseResponse(api.users.getForemansByProjectId.$post({ json: { projectId } })).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
    enabled: !!projectId,
  });

export const userForemansQueryOptions = (filter?: GetAllFilter) =>
  queryOptions({
    queryKey: userQueryKeys.foremans(filter),
    queryFn: () => parseResponse(api.users.getForemans.$post({ json: { filter } })),
  });

export const userOpmsQueryOptions = (directorId?: string) =>
  queryOptions({
    queryKey: userQueryKeys.opms(directorId),
    queryFn: () => parseResponse(api.users.getOpms.$post({ json: { directorId } })),
  });

export const userByRolesQueryOptions = (roles: UserRole[]) =>
  queryOptions({
    queryKey: userQueryKeys.byRoles(roles),
    queryFn: () => fetchUsersByRoles(roles),
    enabled: roles.length > 0,
  });

export const userAllByCompanyIdQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: userQueryKeys.allByCompanyId(companyId),
    queryFn: () =>
      parseResponse(api.users.getAllByCompanyId.$post({ json: { companyId } })).then((items) =>
        items.map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          deletedAt: item.deletedAt ? new Date(item.deletedAt) : null,
        }))
      ),
    enabled: !!companyId,
  });

export const userStatusSelectQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.selectStatus(),
    queryFn: () => Promise.resolve(Object.entries(userStatusNames).map(([id, name]) => ({ id, name }))),
  });

const permissionTypeOptions = [
  { id: 'custom', name: 'Custom Permissions' },
  { id: 'nonScoped', name: 'Non Company Scoped' },
  { id: 'scoped', name: 'Company Scoped' },
] as const;

export const userPermissionsTypeSelectQueryOptions = () =>
  queryOptions({
    queryKey: userQueryKeys.selectPermissionsType(),
    queryFn: async () => [...permissionTypeOptions],
  });
